import { useState } from "react";
import type { Pot } from "@/types";
import { XIcon } from "@/app/components/system/Icons";
import { RoundButton } from "@/app/components/system/RoundButton";
import { Button } from "@/app/components/system/Button";
import { SunHoursPicker } from "@/app/components/SunHoursPicker";
import { isSunRangeInvalid } from "@/app/lib/sunHours";
import { usePots } from "@/app/lib/data-context";

interface SunHoursModalProps {
  pot: Pot;
  onClose: () => void;
}

export function SunHoursModal({ pot, onClose }: SunHoursModalProps) {
  const { editPot } = usePots();
  const [start, setStart] = useState(pot.sunHours?.start ?? "");
  const [end, setEnd] = useState(pot.sunHours?.end ?? "");
  const [saving, setSaving] = useState(false);

  const canSave =
    start.length > 0 && end.length > 0 && !isSunRangeInvalid(start, end);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const save = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await editPot(pot.id, { sunStart: start, sunEnd: end });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={handleClose}
      className="fixed inset-0 z-100 grid place-items-end justify-items-center backdrop-blur-[2px] animate-[loc-fade_0.18s_ease-out]"
      style={{ background: "oklch(0.2 0.02 140 / 0.45)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-130 bg-cream pt-4 pb-6 px-4 flex flex-col rounded-t-[22px] animate-[loc-slide_0.24s_cubic-bezier(0.2,0.8,0.2,1)]"
      >
        <header className="flex items-start justify-between gap-3 pt-1 pb-2 px-1">
          <h3 className="font-display font-medium text-lg m-0 text-ink tracking-tight">{pot.name}</h3>
          <RoundButton onClick={handleClose} aria-label="Close" disabled={saving}>
            <XIcon size={18} />
          </RoundButton>
        </header>

        <p className="font-sans text-[13px] text-ink-2 m-0 mb-4 px-1 leading-snug">
          Telling Plantita when direct sun hits this pot sharpens watering recommendations — sun-baked soil dries faster, so we'll nudge you to water sooner on bright days.
        </p>

        <div className="flex flex-col gap-1 px-1">
          <SunHoursPicker
            start={start}
            end={end}
            onStartChange={setStart}
            onEndChange={setEnd}
            headingStyle="section"
            showDescription={false}
          />
        </div>

        <div className="flex justify-end mt-4 px-1">
          <Button
            onClick={save}
            disabled={!canSave || saving}
            className="disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-ink"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
